/**
 * @file    ProgramItemCard.tsx
 * @scope   VIEW
 * @summary A single program row: collapsible card hosting the "Field details" and "Filters" tabs,
 *          with drag handle, delete action and validation state.
 * @consumes RollupProgramModel, programsManagerModel, Enterprise FiltersTab, ProgramDetailsTab
 * @produces `ProgramItemCard` component
 * @sideEffects None — presentational; edits bubble up via callbacks.
 * @notes Mirrors the Enterprise FieldItemCard (same classes, same tabs behaviour).
 */
import React, { useState } from 'react';
import { Icon } from '@cx/ui';
import Tippy from '@tippyjs/react';
import Accordion from 'core-ui/accordionDrawer/accordionDrawer';
import DataValidationTip from 'components/DataValidation/DataValidationTip';
import { RollupProgramModel } from 'models/CrossProgramRollup/RollupMetricModel';
import { AdvancedFilterFieldConfig } from 'models/WidgetModel';
import FiltersTab from '../../../Enterprise/Views/FieldsManager/FiltersTab';
import { areFiltersInvalid } from '../../../Enterprise/utils/validation/filterValidation';
import {
  ProgramOption,
  QuestionOption,
  isProgramSelectionIncomplete,
} from './programsManagerModel';
import ProgramDetailsTab from './ProgramDetailsTab';
import '../../../Enterprise/Views/FieldsManager/FiltersTab.scss';
import '../../../Enterprise/Views/FieldsManager/FieldCardTabs.scss';
import 'tippy.js/dist/tippy.css';

/** Truncates a string to a maximum length, appending ellipsis if it exceeds the limit. */
const truncateText = (text: string, maxLength = 20): string =>
  text.length > maxLength ? `${text.slice(0, maxLength)}...` : text;

type ProgramItemCardTab = 'details' | 'filters';

interface ProgramItemCardProps {
  program: RollupProgramModel;
  index: number;
  isExpanded: boolean;
  isTouched: boolean;
  /** Catalog still loading — tabs render skeletons. */
  isLoading?: boolean;
  programOptions: ProgramOption[];
  questionOptions: QuestionOption[];
  availableFilterFields: AdvancedFilterFieldConfig[];
  /** When set, the card starts with this tab active (used when returning from filter editing). */
  defaultActiveTab?: ProgramItemCardTab;
  onRemove: (id: string) => void;
  onProgramSelect: (id: string, option: any) => void;
  onQuestionSelect: (id: string, option: any) => void;
  onReorder: (draggedIndex: number, targetIndex: number) => void;
  onAccordionToggle: (id: string) => void;
  onBlur: (e: React.FocusEvent<HTMLDivElement>, id: string) => void;
  onAdvancedSettings?: (id: string) => void;
  /** `filterId` null means "Add". */
  onEditFilter: (id: string, filterId: string | null) => void;
  onFilterRemove: (id: string, filterId: string) => void;
}

const ProgramItemCard: React.FC<ProgramItemCardProps> = ({
  program,
  index,
  isExpanded,
  isTouched,
  isLoading = false,
  programOptions,
  questionOptions,
  availableFilterFields,
  defaultActiveTab,
  onRemove,
  onProgramSelect,
  onQuestionSelect,
  onReorder,
  onAccordionToggle,
  onBlur,
  onAdvancedSettings,
  onEditFilter,
  onFilterRemove,
}) => {
  const [activeTab, setActiveTab] = useState<ProgramItemCardTab>(defaultActiveTab ?? 'details');
  const filters = program.filters ?? [];

  const isDetailsInvalid = isTouched && isProgramSelectionIncomplete(program);
  const isFiltersInvalid = areFiltersInvalid(filters, isTouched);
  const isCardInvalid = isDetailsInvalid || isFiltersInvalid;

  const titleValue = program.displayName?.trim() || `Program ${index + 1}`;
  const taglineValue = program.questionLabel?.trim() || 'No question selected';
  const isTooltipDisabled = !program.displayName?.trim() || !program.questionLabel?.trim();

  return (
    <Tippy
      content={
        <div>
          <div className="tooltip-title">{titleValue}</div>
          <div className="tooltip-text">{taglineValue}</div>
        </div>
      }
      disabled={isTooltipDisabled}
      placement="left"
      className="tooltip-container"
    >
      <div
        tabIndex={-1}
        draggable
        onDragStart={(e) => e.dataTransfer.setData('programIndex', String(index))}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => {
          e.preventDefault();
          onReorder(parseInt(e.dataTransfer.getData('programIndex'), 10), index);
        }}
        onBlur={(e) => onBlur(e, program.id)}
        className={`field-item program-item${
          isCardInvalid ? ' enterprise-container--invalid' : ''
        }`}
      >
        <div className="field-item-header">
          <div className="dragHandle" title="Drag to reorder">
            <Icon name="drag_indicator" />
          </div>
          <div
            className="diy-drawer-icon delete-icon"
            onClick={() => onRemove(program.id)}
            title="Delete program"
          >
            <Icon name="system-delete" />
          </div>
          <div className="field-item-validation">
            {isCardInvalid && (
              <DataValidationTip
                message={
                  isDetailsInvalid && isFiltersInvalid
                    ? 'This program has missing parameters and incomplete filters.'
                    : isFiltersInvalid
                    ? 'One or more filters are missing a selected value.'
                    : 'This program has missing parameters.'
                }
                kind="error"
                placement="bottom"
              />
            )}
          </div>
        </div>

        <Accordion
          title={truncateText(titleValue)}
          tagline={truncateText(taglineValue, 40)}
          arrowIcon={<Icon name="arrow-expand" size={36} />}
          isExpanded={isExpanded}
          onToggle={() => onAccordionToggle(program.id)}
        >
          <div className="field-card-tabs">
            <div className="field-card-tabs__header">
              <button
                type="button"
                className={`field-card-tabs__tab${
                  activeTab === 'details' ? ' field-card-tabs__tab--active' : ''
                }${isDetailsInvalid ? ' field-card-tabs__tab--invalid' : ''}`}
                onClick={() => setActiveTab('details')}
              >
                Program details
              </button>
              <button
                type="button"
                className={`field-card-tabs__tab${
                  activeTab === 'filters' ? ' field-card-tabs__tab--active' : ''
                }${isFiltersInvalid ? ' field-card-tabs__tab--invalid' : ''}`}
                onClick={() => setActiveTab('filters')}
              >
                Filters ({filters.length})
              </button>
            </div>
            <div className="field-card-tabs__content">
              {activeTab === 'details' && (
                <ProgramDetailsTab
                  program={program}
                  isTouched={isTouched}
                  isLoading={isLoading}
                  programOptions={programOptions}
                  questionOptions={questionOptions}
                  onProgramSelect={(option) => onProgramSelect(program.id, option)}
                  onQuestionSelect={(option) => onQuestionSelect(program.id, option)}
                  onAdvancedSettings={
                    onAdvancedSettings ? () => onAdvancedSettings(program.id) : undefined
                  }
                />
              )}
              {activeTab === 'filters' && (
                <FiltersTab
                  filters={filters}
                  availableFilterFields={availableFilterFields}
                  isLoading={isLoading}
                  onEditFilter={(filterId) => onEditFilter(program.id, filterId)}
                  onRemoveFilter={(filterId) => onFilterRemove(program.id, filterId)}
                />
              )}
            </div>
          </div>
        </Accordion>
      </div>
    </Tippy>
  );
};

export default ProgramItemCard;
